import { LitElement, css, html, PropertyValues } from 'lit';
import {property, customElement} from 'lit/decorators.js';
import { DataSeriesEntry, EchartsOptions, TimeseriesChartTimeFormat, YAxisChartParams } from './echarts-options';
import { VoyageStyle } from '../../voyage-style/src/voyage-style';
import '../../voyage-spinner/src/voyage-spinner';
import { FgCircleColor, SpinnerBorderWidth, SpinnerOptions, BgCircleColor } from '../../voyage-spinner/src/voyage-spinner';
import { TooltipType, VoyTimeseriesChartTooltip } from './voy-timeseries-chart-tooltip';
import { ForecastInfo, WeatherModel } from '../../../src/models/weather-model';
import { echartUtils, PilotPointTimestamps, TimeseriesData, TimeseriesValues, TimestampData, TimestampFillerSeriesName, UncertantyBandSeriesNames, WaypointAndSeriesMatching, WaypointData, WaypointTimestamps } from './echartUtils';
import { debounce, GetFormattedDate } from '../../../src/utils/helpers';

export enum ChartColors {
    Actual = "#FA6A55",
    Route1 = "#40AB35",
    Route2 = "#006CB6",
    Route3 = "#A91ADB"
}

interface ChartElements {
    [index: string]: VoyTimeseriesChart
}

// Echarts are currently loaded as global
declare const echarts: any;

// echarts doesn't support dotted/dashed legens lines, so we have to use custom SVG
// A ticket on this is raised here: https://github.com/JohnCoene/echarts4r/issues/188
const dashedLine = 'path://M180 1000 l0 -40 200 0 200 0 0 40 0 40 -200 0 -200 0 0 -40z, M810 1000 l0 -40 200 0 200 0 0 40 0 40 -200 0 -200 0 0 -40zm, M1440 1000 l0 -40 200 0 200 0 0 40 0 40 -200 0 -200 0 0 -40z';

const defaultYaxisChartParams = {
    nameRotate: 90,
    nameLocation: 'middle',
    padding: [0,0,0,0],
    nameGap: 30,
}

@customElement('voy-timeseries-chart')
export class VoyTimeseriesChart extends LitElement {
    @property({ type: Array, attribute: false }) data: TimeseriesData[] = [];
    @property({ type: Number, attribute: false }) markline: number = -1;
    @property({ type: Object, attribute: false }) pilotPoints: PilotPointTimestamps = { departure: -1, arrival: -1 };
    @property({ type: Object, attribute: false }) newPilotPoints: PilotPointTimestamps = { departure: -1, arrival: -1 };
    @property({ type: String, attribute: false }) chartTimeFormat: TimeseriesChartTimeFormat = TimeseriesChartTimeFormat.DayAndMonth;
    @property({ type: Object, attribute: false }) yAxisChartParams?: YAxisChartParams = defaultYaxisChartParams;
    @property({ type: Number, attribute: false }) leftPadding: number = 65;
    @property({ type: Boolean, attribute: false }) displayNoDataMessage: boolean = true;
    @property({ type: String, attribute: false }) tooltipType?: TooltipType;
    @property({ type: Boolean, attribute: "show-legend" }) showLegend: boolean = false;
    @property({ type: String }) unit: string = "";
    @property({ type: String }) waypointAndSeriesMatching: WaypointAndSeriesMatching = WaypointAndSeriesMatching.MatchWaypointsToSeries;
    @property({ type: Boolean }) showSpinner: boolean = false;
    @property({ type: Array }) allTimestamps: TimestampData[] = [];
    @property({ type: Boolean }) showWaypoints: boolean = false;
    @property({ type: Array, attribute: false }) routesAndWaypoints: WaypointTimestamps[][] = [];
    @property({ type: Array, attribute: false }) pilotPointWaypointNumbers: number[][] = [];
    @property({ type: Array, attribute: false }) hiddenSeries: string[] = [];

    constructor() {
        super();

        this._resizeObserver = new ResizeObserver(() => {
            if (this._chart) {
                this._chart.resize();
            }
        });

        this._initialize = this._initialize.bind(this);
        this._assignDataToChart = this._assignDataToChart.bind(this);
        this._debouncedAssignDataToChart = debounce(this._assignDataToChart, 100)
    }

    static get styles() {
        return [
            css`
            :host {
                position: relative;
                display: flex;
                flex-direction: row;
                font-family: 'MaerskText';
                display: flex;
                box-sizing: border-box;
                width: 100%;
                height: 100%;
            }

            .container {
                height: 200px;
                width: 100%;
            }

            .container[height1] {
                height: 223px;
            }
            .container[height2] {
                height: 246px;
            }
            .container[height3] {
                height: 270px;
            }

            #plot {
                display: none;
            }

            #plot[show] {
                display: block;
            }

            .graph {
                position: relative;
                background: var(--neutrals-grey-10);
                width: 100%;
                height: 100%;
            }

            #no-data {
                display: none;
                color: var(--neutrals-grey-03, black);
                font-size: 16px;
                line-height: 50px;
                text-align: center;
            }

            #no-data[show] {
                display: block;
            }

            .chart-loading-wrapper {
                display: none;
                position: absolute;
                height: 100%;
                width: 100%;
                top: 0px;
                bottom: 0px;
                right: 0px;
                left: 0px;
                justify-content: center;
                align-items: center;
                z-index: 50;
            }

            .chart-loading-wrapper[show] {
                display: flex;
            }

            .chart-loading-wrapper p {
                color: var(--functional-grey-800, black);
                margin: 0;
                margin-top: 8px;
            }

            .spinner-indicator {
                display: flex;
                flex-direction: column;
                justify-content: center;
                align-items: center;
            }

            voyage-spinner {
                width: 24px;
                height: 24px;
                left: calc(50% - 25px);
                top: calc(50% - 25px);
            }
        `];
    }

    static _connectedCharts: any[] = [];

    static connect(charts: VoyTimeseriesChart[]) {
        // Wrapped in setTimeout so the charts have all been rendered
        setTimeout(() => {
            const ecs = charts.map(c => c._chart);
            this._connectedCharts = ecs;
            echarts.connect(ecs);
        });
    }

    static updateSeriesVisibility(seriesNames: string[], visible: boolean) {
        // This will imitate a click on the legend on the first chart.
        // Due to the charts being connected, this action will affect all
        // charts (so no need to dispatch event on all charts)
        for (const seriesName of seriesNames) {
            if (visible) {
                this._connectedCharts[0].dispatchAction({
                    type: 'legendSelect',
                    name: seriesName
                })
            } else {
                this._connectedCharts[0].dispatchAction({
                    type: 'legendUnSelect',
                    name: seriesName
                })
            }
        }
    }

    render() {
        return html`
            <div class="container">
                <div class="graph" id="plot" ?show=${this.data.length}></div>
                <div class="graph" id="no-data" ?show=${this.data.length === 0 && !this.showSpinner}>${this.displayNoDataMessage ? "No data" : ""}</div>
                <div class="chart-loading-wrapper" id="spinner" ?show=${this.showSpinner}>${this._renderSpinner()}</div>
            </div>
        `;
    }

    updated(changedProperties: PropertyValues) {
        if (!this._chart) return;

        if (this.data.length == 0) {
            return;
        }

        this._chart.resize();
        changedProperties.forEach((_, propName) => {
            if (propName === 'unit') {
                this._chart.setOption({
                    yAxis: {
                        name: this.unit
                    }
                });
            } else if (propName === 'data' || propName === 'allTimestamps' || propName === 'showWaypoints') {
                if (this._canMerge()) {
                    this._initChartData();
                    this._debouncedAssignDataToChart();
                    this._updateLegend();
                }
                else {
                    this._initialize();
                }

                this._oldData = this.data;

            } else if (propName === 'markline') {
                this._dataSeriesMap.get("currentTimeMarker")!.markLine = {
                    symbol: 'none',
                    data: [{
                        name: 'Current time',
                        xAxis: new Date(this.markline),
                        label: {
                            show: false,
                            "formatter": "Time"
                        },
                        lineStyle: {
                            "color": this._marklineColor,
                            "width": 3,
                            "type": "solid"
                        }
                    }],
                }
                this._debouncedAssignDataToChart();
            } else if (propName === "showLegend") {
                this._updateLegend();
            } else if (propName === "pilotPoints" || propName === "newPilotPoints") {
                this._updatePilotIcons();
                this._debouncedAssignDataToChart();
            } else if (propName === "hiddenSeries") {
                this._setUncertantyBandVisibility();
            }
        });
    }

    firstUpdated() {
        this._initialize();
        VoyageStyle.addEventListener('styles-changed', this._initialize);
        this._resizeObserver.observe(this);
    }

    disconnectedCallback() {
        VoyageStyle.removeEventListener('styles-changed', this._initialize);
        this._resizeObserver!.disconnect();
    }

    private _marklineColor: string = "";
    private _timeSeriesData: Map<string, TimeseriesValues> = new Map();
    private _dataSeriesMap: Map<string, DataSeriesEntry> = new Map();
    private _chart: any = null;
    private _resizeObserver: ResizeObserver;
    private _oldData: TimeseriesData[] | null = null;
    private _tooltipElement: VoyTimeseriesChartTooltip = new VoyTimeseriesChartTooltip();
    private _forecastInfo: ForecastInfo|null = null;
    private _legendConfig: {[index:string]: any} = {};
    private _debouncedAssignDataToChart: () => void;

    private _initialize() {
        const computedStyle = getComputedStyle(this);
        const textColor = computedStyle.getPropertyValue("--neutrals-grey-04") || "black";
        this._marklineColor = computedStyle.getPropertyValue('--performance-viewer-time-markline-color') || 'black';

        this._initChartData();

        const chartElement = this.shadowRoot!.querySelector('#plot');
        if (!this._chart) {
            this._chart = echarts.init(chartElement);
        } else {
            this._chart.clear();
        }

        const dataSeries = Array.from(this._dataSeriesMap.values());

        this._tooltipElement.unit = this.unit;

        const tooltip = echartUtils.createTooltip(this);
        this._updateLegend();

        this._chart.setOption(EchartsOptions.options(dataSeries, textColor, this.unit, this.leftPadding, this.chartTimeFormat, this.yAxisChartParams, tooltip));
    }

    private _renderSpinner() {
        const spinnerOptions: SpinnerOptions = {
            colorFg: FgCircleColor.BLUE,
            colorBg: BgCircleColor.GREY2,
            borderWidth: SpinnerBorderWidth.W1,
        }
        return html`
            <div class="spinner-indicator">
                <voyage-spinner .options=${spinnerOptions}></voyage-spinner>
                <p>Loading</p>
            </div>
        `
    }

    private _assignDataToChart() {
        this._chart.setOption({
            series: Array.from(this._dataSeriesMap.values()),
            legend: this.showLegend ? this._legendConfig : { show: false }
        },
        {
            lazyUpdate: true,
        });
    }

    private _updateLegend() {
        if (this.showLegend) {
            const legend: { [index:string]: any  } = {
                data: [],
                align: 'right',
                selected: {},
                itemHeight: 3,
                itemWidth: 14,
                top: "4%"
            };

            for (let i = 0; i < this.data.length; i++) {
                const dataSeries = this.data[i];
                legend.data.push({
                    name: dataSeries.name,
                    icon: dataSeries.lineDash ? dashedLine : 'rect',
                });
            }
            this._legendConfig = legend;
        }
        else {
            this._legendConfig = {}
        }
    }

    private async _initChartData() {
        this._dataSeriesMap = new Map();
        this._updateEndOfCurrentForecastMarker();
        this._updateCurrentTimeMarker();
        this._updatePilotIcons();
        this._updateData();

        await this.updateComplete;
        if (this._chart) {
            this._setUncertantyBandVisibility();
            VoyTimeseriesChart.updateSeriesVisibility(this.hiddenSeries, false);
        }
    }

    private _updateData() {
        this._timeSeriesData = new Map();
        
        let allTimestamps = this.allTimestamps;
        if (!this.allTimestamps.length) {
            allTimestamps = echartUtils.getAggregatedTimestamps(this.data, this.routesAndWaypoints);
        }

        let computedStyle: CSSStyleDeclaration | null = null;
        for (let i = 0; i < this.data.length; i++) {
            const dataSeries = this.data[i];

            this._timeSeriesData.set(dataSeries.name, echartUtils.prepAndPadTimeseries(allTimestamps, dataSeries.timeseries, i, this.waypointAndSeriesMatching));
            const timeSeriesData = this._timeSeriesData.get(dataSeries.name)!

            let color = dataSeries.color;
            // Check if we need to translate the color
            if (color && color.startsWith("--")) {
                // CSS custom property - need to look up the actual color value
                if (!computedStyle) {
                    computedStyle = getComputedStyle(this);
                }
                color = computedStyle.getPropertyValue(color);
            }

            // Create WP format for the graph
            let waypointData: WaypointData[] = []
            if (this.showWaypoints && dataSeries.name !== 'actual') {
                // Determine which route index this data series corresponds to
                let routeIdx = -1;
                if (dataSeries.name.includes('Route')) {
                    const match = dataSeries.name.match(/Route (\d)/);
                    if (match) {
                        routeIdx = parseInt(match[1]) - 1; // Convert to 0-based index
                    }
                } else if (this.waypointAndSeriesMatching === WaypointAndSeriesMatching.SameWaypointsForAllSeries) {
                    // In execution mode, there's only one route
                    routeIdx = 0;
                }

                const hasWaypoints = timeSeriesData.waypoints?.length;
                if (hasWaypoints) {
                    const pilotWpNumbers = (routeIdx >= 0 && routeIdx < this.pilotPointWaypointNumbers.length) 
                        ? this.pilotPointWaypointNumbers[routeIdx] || []
                        : [];
                    
                    waypointData = timeSeriesData.waypoints!.map((wp) => {
                        return {
                            name: `WP ${wp.wpNumber}`,
                            value: `WP ${wp.wpNumber}`,
                            xAxis: wp.timestamp,
                            yAxis: wp.value,
                            isPilotPoint: pilotWpNumbers.includes(wp.wpNumber),
                            eta: wp.eta
                        }
                    });
                }
            }

            const timestamps = timeSeriesData.timestamps;

            const data = echartUtils.createMainSeries({dataSeries, color, timeSeriesData: timeSeriesData, timestamps, showWaypoints: this.showWaypoints, waypointData});
            this._dataSeriesMap.set(dataSeries.name, data);

            if (timeSeriesData.lower && timeSeriesData.upper) {
                const lowerValues = echartUtils.createLowerSeries({dataSeries, timeSeriesData: timeSeriesData, timestamps});
                this._dataSeriesMap.set(<UncertantyBandSeriesNames>`${dataSeries.name}_lower`, lowerValues);

                const upperValues = echartUtils.createUpperSeries({dataSeries, timeSeriesData: timeSeriesData, timestamps, color});
                this._dataSeriesMap.set(<UncertantyBandSeriesNames>`${dataSeries.name}_upper`, upperValues);
            }
        }

        if (this.allTimestamps.length) {
            const timestampFiller = echartUtils.createTimestampFillerSeries(this.allTimestamps);
            this._dataSeriesMap.set(TimestampFillerSeriesName.TimestampFiller, timestampFiller);
        }
    }

    private _setUncertantyBandVisibility() {
        const defaultHidden = ["time", TimestampFillerSeriesName.TimestampFiller];
        
        for (const key of this._dataSeriesMap.keys()) {
            const line = this._dataSeriesMap.get(key);
            
            if (!line?.data?.length || defaultHidden.includes(line.name!)) {
                continue;
            }

            if (this.hiddenSeries.includes(key)) {
                if (line.name === key) {
                    if (this._dataSeriesMap.has(<UncertantyBandSeriesNames>`${key}_upper`)) {
                        const uncertantyBand = this._dataSeriesMap.get(<UncertantyBandSeriesNames>`${key}_upper`);
                        if (uncertantyBand!.areaStyle) {
                            uncertantyBand!.areaStyle.opacity = 0;
                        }
                    }
                }
            } else {
                if (line.name === key) {
                    if (this._dataSeriesMap.has(<UncertantyBandSeriesNames>`${key}_upper`)) {
                        const uncertantyBand = this._dataSeriesMap.get(<UncertantyBandSeriesNames>`${key}_upper`);
                        if (uncertantyBand!.areaStyle) {
                            uncertantyBand!.areaStyle.opacity = 0.2;
                        }
                    }
                }
                
            }
        }
        
        this._debouncedAssignDataToChart();
    }

    private _updateCurrentTimeMarker() {
        const currentTimeMarker: DataSeriesEntry = echartUtils.createCurrentTimeMarker(this.markline, this._marklineColor);
        this._dataSeriesMap.set("currentTimeMarker", currentTimeMarker);
    }

    private _updateEndOfCurrentForecastMarker() {
        const newForecastInfo: ForecastInfo|null = WeatherModel!.latestForecastInfo;
        if (!newForecastInfo || !newForecastInfo.forecast_to) {
            console.warn("Trying to create end of forecast marker for weather graph, but forecast info is missing - ForecastInfo:", this._forecastInfo);
            return;
        }

        // forecast has not changed, no reason to re-create the marker
        if (this._forecastInfo && this._forecastInfo.forecast_to === newForecastInfo?.forecast_to && this._dataSeriesMap.has("endOfForecastMarker")) {
            return;
        }

        this._forecastInfo = newForecastInfo;
        const endOfForecastMarker: DataSeriesEntry = {
            name: 'end of current forecast',
            type: 'line',
            zlevel: 0,
            showSymbol: false,
            hoverAnimation: false,
            data: [],
            animation: false,
            markLine: this._createEndOfForecastMarker(newForecastInfo)
        };
        this._dataSeriesMap.set("endOfForecastMarker", endOfForecastMarker);
    }

    private _createEndOfForecastMarker(newForecastInfo: ForecastInfo) {
        // Tooltip doesn't show up across charts if all xAxis entries (timestamps)
        // are not the same. This means, if this forecastMarker is added to
        // only weather charts, all charts don't have same xAxis intries, and thus
        // tooltip don't show up on all charts. It's easiest to just add the
        // marker too all charts but only make it visible (opacity) on weather charts
        return {
            symbol: 'none',
            data: [{
                name: "End of current forecast",
                xAxis: new Date(newForecastInfo!.forecast_to),
                label: {
                    show: false,
                    "formatter": "End of current forecast"
                },
                lineStyle: {
                    "color": 'orange',
                    "width": 3,
                    "type": "solid",
                    "smooth": true,
                    "opacity": this.tooltipType === TooltipType.weather ? 1 : 0
                }
            }],
        }
    }

    private _updatePilotIcons() {
        const styles = getComputedStyle(this);
        const color = styles.getPropertyValue('--pilot-point') || 'purple';

        if (this.pilotPoints.departure !== -1) {
            const departurePilotMarker: DataSeriesEntry = {
                name: 'departure pilot',
                type: 'line',
                zlevel: 0,
                color: 'purple',
                showSymbol: false,
                hoverAnimation: false,
                data: [],
                animation: false,
                markLine: this.pilotPoints.departure === -1 ? undefined : {
                    symbol: "none",
                    data: [{
                        name: 'departure pilot',
                        xAxis: new Date(this.pilotPoints.departure),
                        label: {
                            show: true,
                            "formatter": '{pilotIcon|}',
                            rich: {
                                pilotIcon: { // this replaces the matching 'pilotIcon' id inside the above formatter i.e. adding image to the label
                                    height: 10,
                                    width: 10,
                                    backgroundColor: {
                                        image: this.getPilotSvg('purple')
                                    }
                                }
                            }
                        },
                        emphasis: {
                            label: {
                                show: true,
                                position: 'insideEndTop',
                                formatter: `Scheduled pilot
${GetFormattedDate(this.pilotPoints.departure)} UTC`,
                                color: 'purple',
                                fontWeight: 'bold',
                                backgroundColor: 'white',
                                borderColor: 'purple',
                                borderWidth: 1,
                                borderRadius: 2,
                                padding: 4,
                                lineHeight: 14
                            }
                        },
                        lineStyle: {
                            "color": color,
                            "width": 2,
                            "type": "solid",
                            "smooth": true,
                        }
                    }],
                }
            };
            this._dataSeriesMap.set("departurePilotMarker", departurePilotMarker);
        }

        if (this.pilotPoints.arrival !== -1) {
            const arrivalPilotMarker: DataSeriesEntry = {
                name: 'arrival pilot',
                type: 'line',
                zlevel: 0,
                color: 'purple',
                showSymbol: false,
                hoverAnimation: false,
                data: [],
                animation: false,
                markLine: this.pilotPoints.arrival === -1 ? undefined : {
                    symbol: "none",
                    data: [{
                        name: 'arrival pilot',
                        xAxis: new Date(this.pilotPoints.arrival),
                        label: {
                            show: true,
                            "formatter": '{pilotIcon|}',
                            rich: {
                                pilotIcon: {
                                    height: 10,
                                    width: 10,
                                    backgroundColor: {
                                        image: this.getPilotSvg('purple')
                                    }
                                }
                            }
                        },
                        emphasis: {
                            label: {
                                show: true,
                                position: 'insideEndTop',
                                formatter: `Scheduled pilot
${GetFormattedDate(this.pilotPoints.arrival)} UTC`,
                                color: 'purple',
                                fontWeight: 'bold',
                                backgroundColor: 'white',
                                borderColor: 'purple',
                                borderWidth: 1,
                                borderRadius: 2,
                                padding: 4,
                                lineHeight: 14
                            }
                        },
                        lineStyle: {
                            "color": color,
                            "width": 2,
                            "type": "solid",
                            "smooth": true,
                        }
                    }],
                }
            };
            this._dataSeriesMap.set("arrivalPilotMarker", arrivalPilotMarker);
        }

        if (this.newPilotPoints.arrival !== -1) {
            const arrivalPilotMarker: DataSeriesEntry = {
                name: 'new arrival pilot',
                type: 'line',
                zlevel: 0,
                color: '#FFD029',
                showSymbol: false,
                hoverAnimation: false,
                data: [],
                animation: false,
                markLine: this.newPilotPoints.arrival === -1 ? undefined : {
                    symbol: "none",
                    data: [{
                        name: 'arrival pilot',
                        xAxis: new Date(this.newPilotPoints.arrival),
                        label: {
                            show: true,
                            "formatter": '{pilotIcon|}',
                            rich: {
                                pilotIcon: {
                                    height: 10,
                                    width: 10,
                                    backgroundColor: {
                                        image: this.getPilotSvg('#FFD029')
                                    }
                                }
                            }
                        },
                        lineStyle: {
                            "color": '#FFD029',
                            "width": 2,
                            "type": "solid",
                            "smooth": true,
                        }
                    }],
                }
            };
            this._dataSeriesMap.set("newArrivalPilotMarker", arrivalPilotMarker);
        }
    }

    private _canMerge(): boolean {
        if (this._oldData) {
            // See https://github.com/apache/incubator-echarts/issues/6202 - echarts cannot handle removing dataseries via setOption when merging
            // If a time series is being removed, return false
            if (this._oldData.length < this.data.length) {
                return false
            }

            const newNames = this.data.map(d => d.name);
            const oldNames = this._oldData.map(d => d.name);

            for (const oldName of oldNames) {
                if (newNames.findIndex(newName => newName === oldName) === -1) {
                    return false;
                }
            }
        }

        return true;
    }

    //format suitable for echarts to use 
    private getPilotSvg = (color: string) => {
        const svg = `<svg width="11" height="11" viewBox="0 0 11 11" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="5.13009" cy="5.13013" r="4.5352" transform="rotate(1.0907 5.13009 5.13013)" stroke="${color}"/>
                <path d="M6.92804 5.16385L5.21224 0.814514L2.97253 5.08854L5.04793 9.44472L6.92804 5.16385Z" fill="${color}"/>
                </svg>
                `

        return 'data:image/svg+xml;base64,' + btoa(svg)
    }
};
